import * as THREE from 'three';

/**
 * Frame-rate-independent exponential smoothing: moves `current` toward
 * `target` such that after `smoothTimeSeconds` of continuous movement,
 * ~95% of the distance has closed, regardless of the delta time between
 * frames (a fixed per-frame lerp factor like `current.lerp(target, 0.1)`
 * is NOT frame-rate independent — it converges differently at 30fps vs
 * 144fps, which reads as inconsistent "camera lag" across machines).
 *
 * This is the one small piece of camera-smoothing math every future
 * camera mode (behind-ball framing, ball-follow lag, flyover easing) needs
 * — CameraManager/CameraMode's interface doesn't change to accommodate
 * it; modes just call this from their own `update()`.
 */
export function dampVector3(
  current: THREE.Vector3,
  target: THREE.Vector3,
  smoothTimeSeconds: number,
  deltaSeconds: number,
): void {
  const t = 1 - Math.exp(-deltaSeconds / Math.max(0.0001, smoothTimeSeconds));
  current.lerp(target, t);
}

/** Same idea for a scalar (e.g. FOV easing, distance easing). */
export function dampScalar(
  current: number,
  target: number,
  smoothTimeSeconds: number,
  deltaSeconds: number,
): number {
  const t = 1 - Math.exp(-deltaSeconds / Math.max(0.0001, smoothTimeSeconds));
  return current + (target - current) * t;
}
